import { useEffect } from 'react'
import { shouldSkipShortcut } from './keyboard'
import { matchesBinding } from './matchBinding'
import { isInOverlayLayer, isModalOpen } from './overlayLayers'
import { SHORTCUT_REGISTRY, type ShortcutId } from './shortcutRegistry'

interface UseShortcutOptions {
	/** Mod+K / Mod+/ style: fires even while a text field has focus. */
	allowInTextFields?: boolean
	/** Skip while a Radix layer (menu, listbox, popover, dialog) owns the keyboard. */
	skipInOverlay?: boolean
	/** Skip opening while another dialog is already open. */
	requireModalClosed?: boolean
	/**
	 * Skip the built-in shouldSkipShortcut/overlay/modal checks entirely, for a handler whose guard
	 * depends on its own runtime state (e.g. a toggle that only checks `repeat` once it's already open).
	 * The handler is then responsible for every guard it needs.
	 */
	skipDefaultGuards?: boolean
	/** Space/X need capture to claim the key before Radix's own bubble-phase handling. */
	capture?: boolean
	target?: 'window' | 'document'
	enabled?: boolean
}

/**
 * Registers one keydown listener for a binding in SHORTCUT_REGISTRY, applying the same guards every
 * shortcut owner used to hand-roll. The handler still owns its own feature logic; this only resolves
 * "did this key combination happen, and should it be ignored right now."
 */
export function useShortcut(id: ShortcutId, handler: (event: KeyboardEvent) => void, options: UseShortcutOptions = {}) {
	const {
		allowInTextFields = false,
		skipInOverlay = false,
		requireModalClosed = false,
		skipDefaultGuards = false,
		capture = false,
		target = 'window',
		enabled = true,
	} = options
	const { keys, matchByCode, looseModifier } = SHORTCUT_REGISTRY[id]

	useEffect(() => {
		if (!enabled) {
			return
		}
		const eventTarget = target === 'document' ? document : window
		const onKeyDown = (event: Event) => {
			const keyboardEvent = event as KeyboardEvent
			if (!matchesBinding(keyboardEvent, keys, { matchByCode, looseModifier })) {
				return
			}
			if (!skipDefaultGuards) {
				if (shouldSkipShortcut(keyboardEvent, { inTextFields: allowInTextFields })) {
					return
				}
				if (skipInOverlay && isInOverlayLayer(keyboardEvent.target)) {
					return
				}
				if (requireModalClosed && isModalOpen()) {
					return
				}
			}
			handler(keyboardEvent)
		}
		eventTarget.addEventListener('keydown', onKeyDown, capture)
		return () => eventTarget.removeEventListener('keydown', onKeyDown, capture)
	}, [
		enabled,
		keys,
		matchByCode,
		looseModifier,
		allowInTextFields,
		skipInOverlay,
		requireModalClosed,
		skipDefaultGuards,
		capture,
		target,
		handler,
	])
}
